import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useDesignContracts } from "../useHook";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function getUserLabel(user) {
  if (!user) return "-";
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || user.email || `User #${user.id}`;
}

export default function UserDesignContracts() {
  const { id: designContractId } = useParams();
  const { hasPermission } = usePermission();
  const canDownloadUserPdf = hasPermission(
    SystemPermissions.DesignContract.DOWNLOAD_USER_PDF,
  );
  const canShowActions = canDownloadUserPdf;
  const { getUserDesignContracts } = useDesignContracts();

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = {
        offset,
        limit,
      };
      if (designContractId) params.design_contract_id = designContractId;
      const res = await getUserDesignContracts(params);
      setRecords(res?.data || []);
      if (res?.pagination) {
        setPagination(res.pagination);
      }
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        "Failed to load user generated design contracts.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [offset, designContractId]);

  const displayRecords = records;

  const handleDownload = (url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">
            User Generated Design Contract PDFs
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-600">Loading...</div>
      ) : records.length === 0 ? (
        <div className="text-center text-gray-500">
          No user generated PDFs found
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">Contract Title</th>
                    <th className="px-4 py-3 font-semibold">Version</th>
                    <th className="px-4 py-3 font-semibold">Created</th>
                    {canShowActions && (
                      <th className="px-4 py-3 font-semibold text-right">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayRecords.map((row) => {
                    const url = row.file_url || row.file_path;
                    return (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800">
                            {getUserLabel(row.user)}
                          </div>
                          {row.user?.email && (
                            <div className="text-xs text-gray-500">
                              {row.user.email}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800">
                            {row.designContract?.title || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-semibold">
                            v{row.version ?? 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDate(row.created_at || row.createdAt)}
                        </td>
                        {canShowActions && (
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              {canDownloadUserPdf && (
                                <button
                                  onClick={() => handleDownload(url)}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition duration-200"
                                >
                                  <ArrowDownTrayIcon className="h-5 w-5" />
                                  Download
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {pagination.totalRecords > limit && (
            <div className="mt-4">
              <GlobalPagination
                offset={offset}
                limit={limit}
                total={pagination.totalRecords}
                onPageChange={(newOffset) => setOffset(newOffset)}
                className=""
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
